import { clearToken, getToken } from "./token";

export const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export const SIGNED_OUT_EVENT = "athletain:signed-out";

// A request sent on a connection iOS silently dropped while the app sat in the
// background never fails on its own — it waits out the system timeout (~60 s)
// and React Query then retries it. Give up sooner; the retry gets a fresh one.
export const REQUEST_TIMEOUT_MS = 15_000;
export const UPLOAD_TIMEOUT_MS = 60_000;

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const buildQuery = (query) => {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

function authHeaders() {
  const token = getToken();
  if (!token) {
    throw new ApiError(401, "NOT_AUTHENTICATED", "Sesión no encontrada.");
  }
  return { Authorization: `Bearer ${token}` };
}

function handleSignOut(response, auth) {
  if (response.status === 401 && auth) {
    clearToken();
    window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
    throw new ApiError(401, "UNAUTHORIZED", "Sesión expirada o inválida.");
  }
}

const timeoutError = () =>
  new ApiError(0, "TIMEOUT", "El servidor tardó demasiado en responder. Revisa tu conexión.");

// One request with a single deadline covering both the headers and the body.
const request = async (url, init, { timeoutMs, auth, signal }) => {
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener("abort", abortFromCaller, { once: true });
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    let response;
    try {
      response = await fetch(url, { ...init, signal: controller.signal });
    } catch (networkError) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      if (controller.signal.aborted) throw timeoutError();
      // The browser's own text ("Load failed", "Failed to fetch") is English.
      throw new ApiError(
        0,
        "API_UNREACHABLE",
        "No se pudo contactar al servidor. Revisa tu conexión.",
        { cause: networkError.message },
      );
    }

    if (response.status === 204) return null;

    let payload = null;
    try {
      payload = await response.json();
    } catch {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      if (controller.signal.aborted) throw timeoutError();
      payload = null;
    }

    if (response.ok) return payload;

    handleSignOut(response, auth);

    const error = new ApiError(
      response.status,
      payload?.error || "API_ERROR",
      payload?.message || `Error del servidor (${response.status}). Intenta de nuevo.`,
      payload?.details,
    );
    error.payload = payload;
    error.retryAfter = Number(response.headers.get("Retry-After")) || undefined;
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abortFromCaller);
  }
};

export const apiFetch = async (
  path,
  { method = "GET", body, query, auth = true, timeoutMs = REQUEST_TIMEOUT_MS, signal } = {},
) => {
  const headers = { "Content-Type": "application/json" };
  if (auth) Object.assign(headers, authHeaders());
  return request(
    `${API_URL}${path}${buildQuery(query)}`,
    { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined },
    { timeoutMs, auth, signal },
  );
};

export const apiUpload = async (path, formData) =>
  request(
    `${API_URL}${path}`,
    { method: "POST", headers: authHeaders(), body: formData },
    { timeoutMs: UPLOAD_TIMEOUT_MS, auth: true },
  );

/** GET a binary file (e.g. a report PDF) with the session token. Returns the
 *  Blob plus the server's suggested filename. Errors surface as ApiError with
 *  the server's JSON message, exactly like apiFetch. */
export const apiDownload = async (path, { query, timeoutMs = UPLOAD_TIMEOUT_MS } = {}) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    let response;
    try {
      response = await fetch(`${API_URL}${path}${buildQuery(query)}`, {
        headers: authHeaders(),
        signal: controller.signal,
      });
    } catch (networkError) {
      if (controller.signal.aborted) throw timeoutError();
      throw new ApiError(0, "API_UNREACHABLE", "No se pudo contactar al servidor. Revisa tu conexión.", {
        cause: networkError.message,
      });
    }
    if (!response.ok) {
      handleSignOut(response, true);
      const payload = await response.json().catch(() => null);
      throw new ApiError(
        response.status,
        payload?.error || "API_ERROR",
        payload?.message || `Error del servidor (${response.status}). Intenta de nuevo.`,
        payload?.details,
      );
    }
    const disposition = response.headers.get("content-disposition") || "";
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] || "reporte.pdf";
    return { blob: await response.blob(), filename };
  } finally {
    clearTimeout(timer);
  }
};
